<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import { buildPerfusionScanQrPayload, type PerfusionScanQrPayload } from '@/services/perfusionScanQr';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  /** The investigation the perfusion belongs to. */
  pumpCenterId: {
    type: String,
    default: ''
  },
  /** Title of that study, carried in the code so the app can name what it is scanning. */
  studyTitle: {
    type: String,
    default: ''
  },
  /** The case the scan button was clicked on. */
  perfusionId: {
    type: String,
    default: ''
  },
  /** Case label, shown so it's clear which perfusion the code belongs to. */
  perfusionLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible']);

const qrDataUrl = ref('');
const payload = ref<PerfusionScanQrPayload | null>(null);
const generating = ref(false);
const errorMessage = ref('');
const copied = ref(false);
const copyFailed = ref(false);

let copiedTimer: ReturnType<typeof setTimeout> | undefined;

// Rendered at 2x the display size so the code stays sharp, and scannable, on retina screens.
const QR_PIXELS = 560;

const payloadJson = computed(() => (payload.value ? JSON.stringify(payload.value) : ''));

/** Whose keys the code hands over — worth naming, since that is who the app then acts as. */
const signedInAs = computed(() => payload.value?.keys?.email || payload.value?.keys?.name || '');

const generate = async () => {
  generating.value = true;
  errorMessage.value = '';
  qrDataUrl.value = '';

  try {
    // Built fresh on every open, so a re-login picks up the keys now cached rather than whatever
    // was current when the dialog was first mounted.
    payload.value = buildPerfusionScanQrPayload(
      props.pumpCenterId,
      props.perfusionId,
      props.studyTitle,
      props.perfusionLabel
    );

    // Loaded on demand — the encoder is only needed once someone opens this dialog.
    const { toDataURL } = await import('qrcode');
    qrDataUrl.value = await toDataURL(JSON.stringify(payload.value), {
      width: QR_PIXELS,
      margin: 1,
      // The payload carries the user's keys, so it's long. 'M' keeps the modules large enough to
      // scan at this size while still tolerating a scuffed screen.
      errorCorrectionLevel: 'M',
      color: { dark: '#1f2937', light: '#ffffff' }
    });
  } catch (error) {
    payload.value = null;
    errorMessage.value = error instanceof Error && error.message ?
      error.message :
      "Couldn't generate the scan code — try again.";
  } finally {
    generating.value = false;
  }
};

watch(
  // The titles are encoded into the code, so a case renamed while this is open re-generates it.
  () => [props.visible, props.perfusionId, props.studyTitle, props.perfusionLabel] as const,
  () => {
    copied.value = false;
    copyFailed.value = false;
    if (props.visible) {
      void generate();
    }
  },
  { immediate: true }
);

const copyPayload = async () => {
  if (!payloadJson.value) {
    return;
  }

  clearTimeout(copiedTimer);
  copied.value = false;
  copyFailed.value = false;

  try {
    await navigator.clipboard.writeText(payloadJson.value);
    copied.value = true;
  } catch {
    // Clipboard access can be blocked by permissions or a non-secure origin. Say so rather than
    // letting the click look like it did nothing.
    copyFailed.value = true;
  }

  copiedTimer = setTimeout(() => {
    copied.value = false;
    copyFailed.value = false;
  }, 3000);
};

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <Dialog :visible="visible"
    modal
    dismissable-mask
    :draggable="false"
    :style="{ width: '380px' }"
    :breakpoints="{ '480px': '92vw' }"
    @update:visible="emit('update:visible', $event)">
    <template #header>
      <div class="scan-dialog__header">
        <FontAwesomeIcon :icon="['fal', 'qrcode']" class="scan-dialog__header-icon"/>
        <span class="scan-dialog__title">Perfusion scan</span>
      </div>
    </template>

    <div class="scan-dialog">
      <p class="scan-dialog__hint">
        Scan this code with the Perfusion Companion app to start capturing machine readings
        <template v-if="perfusionLabel">for <strong>{{ perfusionLabel }}</strong></template>.
      </p>

      <div class="scan-dialog__code">
        <ProgressSpinner v-if="generating" stroke-width="4" style="width: 48px; height: 48px"/>
        <img v-else-if="qrDataUrl"
          :src="qrDataUrl"
          :alt="`QR code authorising perfusion scans for ${perfusionLabel || perfusionId}`"
          class="scan-dialog__image"/>
        <p v-else class="scan-dialog__error">{{ errorMessage }}</p>
      </div>

      <dl class="scan-dialog__details" v-if="payload">
        <div class="scan-dialog__detail" v-if="payload.studyTitle">
          <dt>Study</dt>
          <dd>{{ payload.studyTitle }}</dd>
        </div>
        <div class="scan-dialog__detail">
          <dt>Pump center</dt>
          <dd>{{ payload.pumpCenterId }}</dd>
        </div>
        <div class="scan-dialog__detail">
          <dt>Perfusion</dt>
          <dd>{{ payload.perfusionLabel || payload.perfusionId }}</dd>
        </div>
        <div class="scan-dialog__detail" v-if="signedInAs">
          <dt>Signed in as</dt>
          <dd>{{ signedInAs }}</dd>
        </div>
      </dl>

      <p class="scan-dialog__note" v-if="payload">
        The code carries your API keys, so anything that scans it can act as you until you sign out.
        Treat it like a password — don't share or screenshot it.
      </p>

      <div class="scan-dialog__actions">
        <button type="button"
          class="scan-dialog__button"
          :disabled="generating"
          @click="generate">
          Regenerate
        </button>
        <button type="button"
          class="scan-dialog__button scan-dialog__button--primary"
          :disabled="!payloadJson"
          @click="copyPayload">
          {{ copied ? 'Details copied' : 'Copy details' }}
        </button>
      </div>

      <p v-if="copyFailed" class="scan-dialog__copy-failed">
        Couldn't copy automatically — scan the code instead.
      </p>

      <span class="scan-dialog__status" role="status" aria-live="polite">
        {{ copied ? 'Scan details copied to clipboard' : '' }}
      </span>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@use '../../../assets/variables.module' as *;
.scan-dialog__header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.scan-dialog__header-icon {
  font-size: 20px;
  color: hsl(210, 11%, 25%);
}
.scan-dialog__title {
  font-size: 16px;
  font-weight: 600;
}
.scan-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.scan-dialog__hint {
  margin: 0;
  color: hsl(210, 9%, 40%);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}
.scan-dialog__code {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 240px;
  height: 240px;
  padding: 10px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 12px;
  background-color: white;
}
.scan-dialog__image {
  width: 100%;
  height: 100%;
  // Keeps the modules crisp instead of blurring them, which hurts scanning.
  image-rendering: pixelated;
}
.scan-dialog__error {
  margin: 0;
  padding: 0 12px;
  color: hsl(0, 65%, 45%);
  font-size: 13px;
  text-align: center;
}
.scan-dialog__details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  margin: 0;
}
.scan-dialog__detail {
  display: flex;
  gap: 8px;
  font-size: 12px;
  line-height: 16px;

  dt {
    flex: 0 0 84px;
    color: hsl(210, 9%, 50%);
  }

  dd {
    margin: 0;
    color: hsl(210, 11%, 25%);
    word-break: break-all;
  }
}
.scan-dialog__note {
  margin: 0;
  color: hsl(210, 9%, 50%);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}
.scan-dialog__actions {
  display: flex;
  gap: 8px;
  width: 100%;
}
.scan-dialog__button {
  flex: 1;
  padding: 9px 12px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 8px;
  background-color: white;
  color: hsl(210, 11%, 15%);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: hsl(210, 20%, 96%);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}
.scan-dialog__button--primary {
  border-color: transparent;
  background-color: $primary-color;
  color: white;

  &:hover:not(:disabled) {
    background-color: hsl(248.5, 100%, 46%);
  }
}
.scan-dialog__copy-failed {
  margin: -8px 0 0;
  color: hsl(0, 65%, 45%);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}
// Announced to screen readers without taking up layout space.
.scan-dialog__status {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
